<template>
    <div :class="{ 'debug-media-player': debug }">
        <ul v-if="debug" style="font-size: 12px; ">
            <li>Item id: {{ currentItem ? currentItem.id : 'None' }}</li>
            <li>Transition start: {{ currentItem ? currentItem.transitionStart : '-' }}</li>
            <li>Transition end: {{ currentItem ? currentItem.transitionEnd : '-' }}</li>
            <li>Changing: {{ isChangingItem }}</li>
            <li>
                <button @click="resize">Force resize</button>
            </li>
        </ul>

        <div ref="player" class="channel-media-player"></div>
        <div ref="videoPlayers" class="channel-media-video-players"></div>
    </div>
</template>

<script>
    import { mapState } from 'vuex';
    import { cloneDeep, debounce } from 'lodash';
    import videojs from 'video.js';
    import '../../plugins/videojs/webrtc-hash/plugin';
    import { BACKGROUND_TYPE, MEDIA_TYPES, RENDER_STATES, RENDER_TYPES, TRANSITION_TYPE } from '../enums';
    import TRANSITIONS from './transitions';
    import { Application, Assets, BlurFilter, Container, Filter, Sprite, Text, TextStyle, Texture } from 'pixi.js';
    import gsap from 'gsap';
    import { Spector } from 'spectorjs';
    import { PLAYER_COMMUNICATION } from '../../enums/player-communication';

    export default {

        props: {
            communication: {
                type: String,
                default: PLAYER_COMMUNICATION.WEBSOCKET,
            },

            muted: {
                type: Boolean,
                default: false,
            },

            debug: {
                type: Boolean,
                default: false,
            },
        },

        data() {
            return {
                renderer: {
                    app: null,
                    container: null,
                },

                newItem: null,

                currentItem: null,
                isChangingItem: false,

                autoPlay: true,

                playerWidth: null,
                playerHeight: null,
            };
        },

        computed: {
            ...mapState({
                ceremony: state => state.ceremony,
            }),
        },

        watch: {
            debug() {
                this.toggleDebug();
            },
        },

        async mounted() {
            await this.setupRenderer();
            this.resize();

            this.toggleDebug();

            window.addEventListener('resize', this.resizeWindow);
        },

        methods: {
            setActiveItem({ item, options, duration }) {
                // Nothing to play
                if (item === null) {
                    this.changeItem(null, null, duration);
                } else {
                    this.changeItem(cloneDeep(item), options, duration);
                }

                this.renderer.playerIndicator.visible = !item;
            },

            setRenderState({ renderState }) {
                if (this.isChangingItem && renderState === RENDER_STATES.FULL) {
                    return;
                }

                if (renderState === RENDER_STATES.FULL || renderState === RENDER_STATES.AUDIO_ONLY) {
                    this.play();
                } else {
                    this.pause(renderState === RENDER_STATES.MEDIA_ONLY);
                }
            },

            async setupRenderer() {
                const app = new Application({
                    resolution: 1,
                    autoDensity: true,

                    resizeTo: this.$refs.player,

                    // Set to 0.9999, when we set the value to 1 the toggling stops working.
                    // The value is set automatically to 1 in the toggleDebug.
                    backgroundAlpha: 0.99999,

                    powerPreference: 'high-performance',
                });

                this.$refs.player.appendChild(app.view);

                const container = new Container();

                container.width = app.screen.width;
                container.height = app.screen.height;

                app.stage.addChild(container);

                const playerIndicator = await this.createPlayerIndicator();
                container.addChild(playerIndicator);

                this.renderer = {
                    app,
                    container,
                    playerIndicator,
                    videojs,
                };
            },

            toggleDebug() {
                if (this.debug) {
                    console.log('Enabling debug mode');

                    // Only initialize spector when not initialized
                    if (!this.renderer.spector) {
                        const spector = new Spector();
                        spector.displayUI();

                        this.renderer.spector = spector;
                    }

                    videojs.log.level('all');
                    window.renderer = this.renderer;
                    this.renderer.app.renderer.background.alpha = 0;
                } else {
                    this.renderer.app.renderer.background.alpha = 1;

                    if (this.renderer.spector) {
                        this.renderer.spector.getCaptureUI().hide();
                    }
                }
            },

            async createPlayerIndicator() {
                const indicatorContainer = new Container();

                indicatorContainer.id = (new Date()).getTime();
                indicatorContainer.renderType = RENDER_TYPES.PLAYER_INDICATOR;

                const style = new TextStyle({
                    fontWeight: 'bold',
                    opacity: 0.3,
                    fontSize: 20,
                    fill: '#ffffff4d',
                });

                const text = new Text((window.showActivePlayerIndicator || '').toUpperCase(), style);

                text.x = -20;
                text.y = -20;
                text.anchor.set(1);

                indicatorContainer.addChild(text);

                return indicatorContainer;
            },

            async getAsset(item) {
                if (this.communication === PLAYER_COMMUNICATION.IPC) {
                    // For the lite player we have to download the file ourselves because we use a custom url schema for retrieving the locally saved file.
                    const response = await this.$http.get(item.src, {
                        responseType: 'blob',
                    });

                    return Assets.load({
                        src: URL.createObjectURL(response.data),
                        loadParser: 'loadTextures',
                    });
                }

                return await Assets.load(item.src);
            },

            async renderImage(item) {
                const itemContainer = new Container();

                itemContainer.id = (new Date()).getTime();
                itemContainer.renderType = RENDER_TYPES.ITEM;

                const asset = await this.getAsset(item);

                // When the asset fails to load we return an black image to make sure the full block will render
                if (!asset) {
                    const blackTexture = new Sprite(Texture.WHITE);
                    blackTexture.tint = '#111111';

                    itemContainer.addChild(blackTexture);
                    itemContainer.addChild(blackTexture);
                } else {
                    const mainItem = Sprite.from(asset);
                    const backgroundItem = await this.renderBackground(item, asset);

                    mainItem.anchor.set(0.5);

                    this.updateMainRenderSize(mainItem);

                    itemContainer.addChild(backgroundItem);
                    itemContainer.addChild(mainItem);
                }

                this.addTransitionFilter(itemContainer, item);

                this.renderer.container.addChild(itemContainer);

                return itemContainer;
            },

            async renderVideo(item, videoElement) {
                const itemContainer = new Container();

                itemContainer.id = (new Date()).getTime();
                itemContainer.renderType = RENDER_TYPES.ITEM;

                const mainItem = await Sprite.from(videoElement, {
                    resourceOptions: {
                        autoPlay: false,

                        // Disable the autoload since we want to load the resource ourselves and do not let pixi handle the loading async in the constructor.
                        autoLoad: false,
                    },
                });

                // We load the resource ourselves since we want to wait for the contents to load.
                if (mainItem?.texture?.baseTexture?.resource) {
                    await mainItem?.texture?.baseTexture?.resource.load();
                }

                const backgroundItem = await this.renderBackground(item, new Texture(mainItem.texture));

                mainItem.anchor.set(0.5);

                this.updateMainRenderSize(mainItem);

                itemContainer.addChild(backgroundItem);
                itemContainer.addChild(mainItem);

                this.addTransitionFilter(itemContainer, item);

                this.renderer.container.addChild(itemContainer);

                return itemContainer;
            },

            addTransitionFilter(container, item) {
                const transition = this.getTransitionType(item.transitionType);

                if (!transition) {
                    return;
                }

                container.transitionData = cloneDeep(transition.getUniformBaseData(item));
                container.filters = [
                    new Filter(
                        transition.getVertexShader(item),
                        transition.getFragmentShader(item),
                        container.transitionData,
                    ),
                ];
            },

            async renderBackground(item, mainSrc) {
                const backgroundItem = await this.createBackgroundSprite(item, mainSrc);

                if (!backgroundItem) {
                    return;
                }

                backgroundItem.anchor.set(0.5);

                this.updateBackgroundRenderSize(backgroundItem);

                return backgroundItem;
            },

            async createBackgroundSprite(item, mainSrc) {
                switch (item.background?.type) {
                    case BACKGROUND_TYPE.COLOR:
                        const colorSprite = new Sprite(Texture.WHITE);
                        colorSprite.tint = item.background.color;

                        return colorSprite;
                    case BACKGROUND_TYPE.IMAGE:
                        const backgroundTexture = await Assets.load(item.background.image);
                        const fixedBackgroundPhoto = Sprite.from(backgroundTexture);

                        const fixedBackgroundBlur = new BlurFilter();
                        fixedBackgroundBlur.blur = this.ceremony?.company?.image_blur_amount || 20;

                        fixedBackgroundPhoto.filters = [
                            fixedBackgroundBlur,
                        ];

                        return fixedBackgroundPhoto;
                    case BACKGROUND_TYPE.DYNAMIC_BACKGROUND:
                        const currentPhotoBackground = Sprite.from(mainSrc, {
                            resourceOptions: {
                                autoPlay: false,
                            },
                        });

                        const currentPhotoBlur = new BlurFilter();
                        currentPhotoBlur.blur = this.ceremony?.company?.image_blur_amount || 20;

                        currentPhotoBackground.filters = [
                            currentPhotoBlur,
                        ];

                        return currentPhotoBackground;
                }
            },

            getTextureScaleBasedOnScreen(sprite) {
                const texture = sprite.texture;

                const screenWidth = this.renderer.app.screen.width;
                const screenHeight = this.renderer.app.screen.height;

                const textureWidth = texture.orig ? (texture?.width || 0) : 0;
                const textureHeight = texture.orig ? (texture?.height || 0) : 0;

                const renderWidth = Math.max(screenWidth, screenWidth - textureWidth);
                const renderHeight = Math.max(screenHeight, screenHeight - textureHeight);

                const scaleX = renderWidth / textureWidth;
                const scaleY = renderHeight / textureHeight;

                return {
                    scaleX,
                    scaleY,
                };
            },

            updateMainRenderSize(sprite) {
                const { scaleX, scaleY } = this.getTextureScaleBasedOnScreen(sprite);
                const scale = Math.min(scaleX, scaleY);

                sprite.scale.x = scale;
                sprite.scale.y = scale;
            },

            updateBackgroundRenderSize(sprite) {
                const { scaleX, scaleY } = this.getTextureScaleBasedOnScreen(sprite);
                const scale = Math.max(scaleX, scaleY);

                sprite.scale.x = scale;
                sprite.scale.y = scale;
            },

            updatePlayerIndicatorSize() {
                const screenWidth = this.renderer.app.screen.width;
                const screenHeight = this.renderer.app.screen.height;

                this.renderer.playerIndicator.x = (screenWidth / 2);
                this.renderer.playerIndicator.y = (screenHeight / 2);
            },

            getTransitionType(transitionType) {
                if (TRANSITIONS.hasOwnProperty(transitionType)) {
                    return TRANSITIONS[transitionType];
                }

                return TRANSITIONS[TRANSITION_TYPE.CROSS_FADE];
            },

            getCurrentItems() {
                return (this.renderer?.container?.children || []).filter(child => child.renderType === RENDER_TYPES.ITEM);
            },

            getCurrentActiveItems() {
                return this.getCurrentItems().filter(child => {
                    return child.item?.id === this.currentItem?.id;
                });
            },

            async transitionEndSprite(sprite, muteOnly = false, removeAfter = true, options = null) {
                return new Promise((resolve, reject) => {
                    if (sprite.transitionStartAnimation?.isActive()) {
                        sprite.transitionStartAnimation.pause();
                        sprite.transitionStartAnimation.kill();
                    }

                    const transition = this.getTransitionType(sprite.item.transitionType);

                    if (!transition) {
                        resolve();

                        this.removedRenderedChild(sprite);

                        return;
                    }

                    const duration = (options?.transitionEnd ?? sprite.item.transitionEnd) / 1000;

                    this.$emit('transition-end-started', sprite.item.id);

                    const self = this;
                    console.log('[media][renderer] Start transition end for ' + sprite.item.id + ', duration: ' + duration + ', type: ' + sprite.item.transitionType + ', transition data: ' + this.stringifyTransitionData(sprite.transitionData));
                    sprite.transitionEndAnimation = gsap.to(muteOnly ? {} : sprite.transitionData, {
                        ...cloneDeep(muteOnly ? {} : transition.getUniformEndData(sprite.item)),

                        duration,

                        onUpdate() {
                            if (!sprite.videoJs) {
                                return;
                            }

                            const progress = this.progress();
                            const percentage = Math.floor(progress * 100);
                            const playerPercentage = Math.floor((1 - self.getCurrentVolume(sprite.videoJs)) * 100);

                            if (playerPercentage !== 100 && playerPercentage !== percentage && !sprite.item.muted) {
                                self.setPlayerVolume(sprite.item, sprite.videoJs, 1 - progress);
                            }
                        },

                        onComplete: () => {
                            // After the fade update the previous item and emit the transition ended event
                            this.$emit('transition-end-ended', sprite.item.id);

                            if (removeAfter) {
                                this.removedRenderedChild(sprite);
                            }

                            this.pausePlayer(sprite.item, sprite.videoJs);

                            resolve();

                            console.log('[media][renderer] Completed transition end for ' + sprite.item.id);
                        },

                        onInterrupt: () => {
                            if (removeAfter) {
                                this.removedRenderedChild(sprite);
                            }

                            this.pausePlayer(sprite.item, sprite.videoJs);

                            reject();
                        },

                    });

                    sprite.transitionEndStop = () => {
                        sprite.transitionEndAnimation.pause();
                        reject();
                    };
                });
            },

            async transitionStartSprite(sprite, options = null) {
                return new Promise(async (resolve, reject) => {
                    const transition = this.getTransitionType(sprite.item.transitionType);

                    if (!transition) {
                        resolve();

                        return;
                    }

                    if (sprite.videoJs) {
                        this.resize();
                        sprite.videoJs.play();
                    }

                    if (sprite.transitionStartAnimation?.isActive()) {
                        sprite.transitionStartAnimation.pause();
                    }

                    if (sprite.transitionEndAnimation?.isActive()) {
                        sprite.transitionEndAnimation.pause();
                    }

                    const duration = (options?.transitionStart ?? sprite.item.transitionStart) / 1000;
                    console.log('[media][renderer] Start transition start for ' + sprite.item.id + ', duration: ' + duration + ', type: ' + sprite.item.transitionType + ', transition data: ' + this.stringifyTransitionData(sprite.transitionData));

                    this.$emit('transition-start-started', sprite.item.id);

                    const self = this;
                    sprite.transitionStartAnimation = gsap.to(sprite.transitionData, {
                        ...cloneDeep(transition.getUniformStartData(sprite.item)),

                        duration,

                        onUpdate() {
                            if (!sprite.videoJs) {
                                return;
                            }

                            const progress = this.progress();
                            const percentage = Math.floor(progress * 100);
                            const playerPercentage = Math.floor(self.getCurrentVolume(sprite.videoJs) * 100);

                            if (playerPercentage !== 100 && playerPercentage !== percentage && !sprite.item.muted) {
                                self.setPlayerVolume(sprite.item, sprite.videoJs, progress);
                            }
                        },
                        onComplete: function() {
                            this.$emit('transition-start-ended', sprite.item.id);

                            resolve();

                            console.log('[media][renderer] Completed transition start for ' + sprite.item.id);
                        }.bind(this),
                    });

                    sprite.transitionStartStop = () => {
                        sprite.transitionStartAnimation.pause();
                        reject();
                    };
                });
            },

            async createSpriteFromItem(item, duration) {
                if (item?.type === MEDIA_TYPES.IMAGE) {
                    const imageSprite = await this.renderImage(item);
                    imageSprite.item = item;

                    return imageSprite;
                } else if (item?.type === MEDIA_TYPES.VIDEO || item?.type === MEDIA_TYPES.LIVE) {
                    const { player, element } = await this.createVideoPlayer(item, duration);

                    const videoSprite = await this.renderVideo(item, element);

                    // Pause the player since pixijs starts the player...
                    player.pause();

                    videoSprite.videoJs = player;
                    videoSprite.item = item;

                    return videoSprite;
                }

                return null;
            },

            async changeItem(item, options = null, duration = 0) {
                console.log('[media][renderer] Change media item to: ' + item?.id + ', current: ' + this.currentItem?.id);

                this.isChangingItem = true;

                const currentSprites = this.getCurrentItems();

                console.log('[media][renderer] Found ' + currentSprites.length + ' sprites to remove from rendering');

                this.newItem = item;

                const isContentChanged = this.isContentChanged(this.newItem, this.currentItem);
                const sprite = item ? await this.createSpriteFromItem(this.newItem, duration) : null;

                if (isContentChanged && currentSprites.length) {
                    try {
                        const promiseToWait = Promise.all(currentSprites.map(currentSprites => this.transitionEndSprite(currentSprites, false, true, options)));

                        const shouldWait = this.newItem ? this.getTransitionType(this.newItem.transitionType)?.shouldWaitForTransitionEnd() : false;

                        // Only await when we should wait for the transition to end
                        if (shouldWait) {
                            await promiseToWait;
                        }
                    } catch (exception) {
                        // Exception means we stopped the transition early
                        console.error(exception);

                        this.cleanup();
                    }

                    this.$emit('transition-change-finished', item?.id || null);
                }

                this.currentItem = this.newItem;

                if (this.newItem) {
                    if (sprite) {
                        await this.transitionStartSprite(sprite, options);
                    } else {
                        this.currentItem = null;
                    }
                }

                this.isChangingItem = false;
            },

            isContentChanged(newItem, currentItem) {
                if (!newItem && !currentItem) {
                    return false;
                }

                // Check if the content is changed, when there is no change we do not need to hide the current item
                if (newItem && currentItem && currentItem.src && newItem.src) {
                    return currentItem.src !== newItem.src;
                }

                return true;
            },

            cleanup() {
                console.log('[media] Cleanup');

                for (const child of this.getCurrentItems()) {
                    if (child.item?.id === this.currentItem?.id) {
                        continue;
                    }

                    if (child.item?.type === 'video') {
                        setTimeout(() => {
                            this.removedRenderedChild(child);
                        }, child.item.transitionEnd);
                    } else {
                        this.removedRenderedChild(child);
                    }
                }
            },

            async removedRenderedChild(child) {
                console.log('[media] Removing child: ' + child.item?.id);

                if (child.transitionStartAnimation?.isActive()) {
                    child.transitionStartAnimation.pause();
                    child.transitionStartAnimation.kill();
                }

                if (child.transitionEndAnimation?.isActive()) {
                    child.transitionEndAnimation.pause();
                    child.transitionEndAnimation.kill();
                }

                if (child.videoJs && !child.videoJs.isDisposed()) {
                    child.videoJs.dispose();
                }

                child.destroy();
            },

            async createVideoPlayer(item, duration) {
                const element = document.createElement('video');
                element.classList.add('video-js');
                element.classList.add('vjs-default-skin');
                element.classList.add('vjs-16-9');

                this.$refs.videoPlayers.appendChild(element);

                const player = videojs(element, {
                    autoplay: false,
                    loop: item.repeat,

                    muted: this.muted,

                    controls: true,
                    controlBar: {
                        fullscreenToggle: false,
                        pictureInPictureToggle: false,
                    },

                    preload: this.preload,

                    width: '100%',
                    height: '30px',

                    sources: [
                        {
                            src: item.src,
                            type: item.type === MEDIA_TYPES.VIDEO ? 'application/x-mpegURL' : 'webrtc-hash',
                            config: item.streamingConfig,
                        },
                    ],
                });

                player.volume(0);
                player.currentTime(duration || 0);

                if (item.type === MEDIA_TYPES.LIVE) {
                    await new Promise(resolve => {
                        setTimeout(() => {
                            resolve();
                        }, 1000);
                    });
                }

                return {
                    player,
                    element: player.el_.querySelector('video'),
                };
            },

            async play() {
                this.getCurrentActiveItems().map(child => this.transitionStartSprite(child));
            },

            async pause(muteOnly = false) {
                this.getCurrentItems().map(child => this.transitionEndSprite(child, muteOnly, false));
            },

            getCurrentVolume(player) {
                if (!player || !player.el_) {
                    return 0;
                }

                return player.volume();
            },

            setPlayerVolume(item, player, volume) {
                let newVolume = volume >= 1 ? 1 : volume;

                if (newVolume <= 0 || isNaN(newVolume)) {
                    newVolume = 0;
                }

                player.volume(newVolume);
                console.log('[media] Setting volume (' + item.id + '): ' + player.volume().toFixed(2) + '/1');
            },

            pausePlayer(item, player) {
                if (player && player.el_) {
                    console.log('[media] Pausing player (' + item.id + ')');
                    player.pause();
                }
            },

            resizeWindow: debounce(function() {
                // For window events we want a short delay
                this.resize();
            }, 1000),

            resizeInterval() {
                if (this.playerWidth !== this.$refs.player.clientWidth || this.playerHeight !== this.$refs.player.clientHeight) {
                    console.log('Resize width and height not the same, resizing');

                    if (this.renderer.app) {
                        this.renderer.app.resize();
                    }

                    this.resize();
                }
            },

            resize() {
                if (this.renderer.app?.screen) {
                    const screenWidth = this.renderer.app.screen.width;
                    const screenHeight = this.renderer.app.screen.height;

                    // Move container to the center
                    this.renderer.container.x = screenWidth / 2;
                    this.renderer.container.y = screenHeight / 2;

                    for (const child of this.getCurrentItems()) {
                        // Child 1 is always the main item, child 0 is the background
                        this.updateBackgroundRenderSize(child.children[0]);
                        this.updateMainRenderSize(child.children[1]);
                    }

                    this.updatePlayerIndicatorSize();
                }

                if (this.$refs.player) {
                    this.playerWidth = this.$refs.player.clientWidth;
                    this.playerHeight = this.$refs.player.clientHeight;
                }
            },

            stringifyTransitionData(data) {
                if (!this.debug) {
                    return 'debug mode disabled';
                }

                const cache = [];

                return JSON.stringify(data, (key, value) => {
                    // Filter out private properties
                    if (key.startsWith('_')) {
                        return undefined;
                    }

                    // Filter out circular references
                    if (typeof value === 'object' && value !== null) {
                        if (cache.indexOf(value) !== -1) {
                            // Circular reference found, discard key
                            return;
                        }
                        // Store value in our collection
                        cache.push(value);
                    }

                    return value;
                });
            },
        },

        beforeDestroy() {
            window.removeEventListener('resize', this.resizeWindow);

            // Remove all children
            Promise.all((this.renderer?.container?.children || []).map(async itemToRemove => {
                return await this.removedRenderedChild(itemToRemove);
            })).then(() => {
                // After removing all children remove the pixijs instance
                if (this.renderer?.app) {
                    this.renderer.app.destroy(true);
                }
            });
        },
    };

</script>

<style lang="scss">

    .channel-media-player {
        position: absolute;
        width: 100%;
        height: 100%;
        top: 0;
        left: 0;
        overflow: hidden;
    }

    .channel-media-video-players {
        position: fixed;
        bottom: 0;
        left: 0;
        width: 100%;
        display: none;
        align-items: flex-end;
        pointer-events: none;


        .video-js {
            pointer-events: all;
            min-height: 130px;
            width: 200px;
            padding: 0 !important;
        }
    }

    .debug-media-player {
        > ul {
            z-index: 100;
            background: #ffff;
            position: fixed;
            top: 0;
            left: 0;
            padding: 10px;
        }

        .channel-media-video-players {
            display: flex;
        }
    }
</style>
